import { ReactNode, ComponentProps } from 'react';
import { withStyles, MaterialSymbol } from 'vopay';
import styles from './Chip.module.scss';

const cn = withStyles(styles);

type IconProps = ComponentProps<typeof MaterialSymbol>;

type ChipProps = {
  disabled?: boolean;
  label: ReactNode;
  onClick?: () => void;
  /**
   * Marks the chip as a toggle and says whether it is the selected option of a filter group.
   * Leave it unset for a plain clickable chip — that keeps `aria-pressed` off the button, so a
   * chip that isn't a toggle is not announced as an unpressed one.
   */
  isActive?: boolean;
  size?: 'sm' | 'md';
  iconLeft?: IconProps['icon'];
  iconLeftProps?: IconProps;
  iconRight?: IconProps['icon'];
  iconRightProps?: IconProps;
  className?: string;
};

export default function Chip({
  disabled = false,
  label,
  onClick,
  isActive,
  size = 'sm',
  iconRight,
  iconRightProps,
  iconLeft,
  iconLeftProps,
  className,
}: ChipProps) {
  // Only 'md' has its own rules — `.chip` already carries the small sizing, and withStyles would
  // emit an unhashed global "sm" class for a key the module doesn't define
  const chipClassName = cn('chip', size === 'md' && 'md', isActive && 'active', className);

  function renderContents() {
    return (
      <>
        {iconLeft && <MaterialSymbol className={cn('icon')} icon={iconLeft} />}
        {iconLeftProps && <MaterialSymbol {...iconLeftProps} className={cn('icon', iconLeftProps.className)} />}
        <div className={cn('label')}>{label}</div>
        {iconRight && <MaterialSymbol className={cn('icon')} icon={iconRight} />}
        {iconRightProps && <MaterialSymbol {...iconRightProps} className={cn('icon', iconRightProps.className)} />}
      </>
    );
  }

  if (onClick && !disabled) {
    return (
      <button type="button" className={chipClassName} aria-pressed={isActive} onClick={onClick}>
        {renderContents()}
      </button>
    );
  }

  return <div className={chipClassName}>{renderContents()}</div>;
}
